const fieldDisplayConfig = {
  infographics: { label: 'Infographics', icon: 'bi-image' },
  aplus_content: { label: 'A+ Content', icon: 'bi-file-earmark-richtext' },
  ugc_videos: { label: 'UGC Videos', icon: 'bi-play-circle' },
  bullet_points: { label: 'Bullet Points', icon: 'bi-list-ul' },
  description: { label: 'Description', icon: 'bi-file-text' },
};

const extractFilenames = (urls) => {
  if (!Array.isArray(urls)) return new Set();
  return new Set(
    urls
      .map(url => {
        try {
          const pathname = new URL(url).pathname;
          return pathname.split('/').pop().split('?')[0];
        } catch {
          return '';
        }
      })
      .filter(Boolean)
  );
};

const diffMediaSnapshot = (snapshot, currentMedia) => {
  if (!snapshot) return null;

  const changedFields = [];

  // Infographics: count + filename set
  const prevInfographicsFilenames = new Set(snapshot.infographicsFilenames || []);
  const currentInfographicsFilenames = extractFilenames(currentMedia.infographics || []);
  const currentInfographicsCount = (currentMedia.infographics || []).length;

  if (
    snapshot.infographicsCount !== currentInfographicsCount ||
    !setEquals(prevInfographicsFilenames, currentInfographicsFilenames)
  ) {
    changedFields.push({
      field: 'infographics',
      label: fieldDisplayConfig.infographics.label,
      icon: fieldDisplayConfig.infographics.icon,
      type: 'count',
      before: snapshot.infographicsCount,
      after: currentInfographicsCount,
      delta: currentInfographicsCount - snapshot.infographicsCount > 0 ? `+${currentInfographicsCount - snapshot.infographicsCount}` : `${currentInfographicsCount - snapshot.infographicsCount}`,
    });
  }

  // A+ Content: count + hash
  const currentAplusHash = hashString(JSON.stringify(currentMedia.aplus_content || []));
  const currentAplusCount = (currentMedia.aplus_content || []).length;

  if (
    snapshot.aplusModulesCount !== currentAplusCount ||
    snapshot.aplusContentHash !== currentAplusHash
  ) {
    changedFields.push({
      field: 'aplus_content',
      label: fieldDisplayConfig.aplus_content.label,
      icon: fieldDisplayConfig.aplus_content.icon,
      type: snapshot.aplusModulesCount === currentAplusCount ? 'content' : 'count',
      before: snapshot.aplusModulesCount,
      after: currentAplusCount,
      delta: snapshot.aplusModulesCount === currentAplusCount ? null : currentAplusCount - snapshot.aplusModulesCount > 0 ? `+${currentAplusCount - snapshot.aplusModulesCount}` : `${currentAplusCount - snapshot.aplusModulesCount}`,
    });
  }

  // UGC Videos: count + content hash
  const currentUgcCount = (currentMedia.ugc_videos || []).length;
  const currentUgcHash = hashString((currentMedia.ugc_videos || []).map(v => v.url || v.title || '').sort().join('|'));
  const ugcCountChanged = snapshot.ugcVideosCount !== currentUgcCount;
  const ugcContentChanged = snapshot.ugcVideosHash != null && snapshot.ugcVideosHash !== currentUgcHash;

  if (ugcCountChanged || ugcContentChanged) {
    changedFields.push({
      field: 'ugc_videos',
      label: fieldDisplayConfig.ugc_videos.label,
      icon: fieldDisplayConfig.ugc_videos.icon,
      type: ugcCountChanged ? 'count' : 'content',
      before: snapshot.ugcVideosCount,
      after: currentUgcCount,
      delta: ugcCountChanged
        ? (currentUgcCount - snapshot.ugcVideosCount > 0 ? `+${currentUgcCount - snapshot.ugcVideosCount}` : `${currentUgcCount - snapshot.ugcVideosCount}`)
        : null,
    });
  }

  // Bullet Points: count + hash
  const currentBulletPointsHash = hashString((currentMedia.bullet_points || []).join(' '));
  const currentBulletPointsCount = (currentMedia.bullet_points || []).length;

  if (
    snapshot.bulletPointsCount !== currentBulletPointsCount ||
    snapshot.bulletPointsHash !== currentBulletPointsHash
  ) {
    changedFields.push({
      field: 'bullet_points',
      label: fieldDisplayConfig.bullet_points.label,
      icon: fieldDisplayConfig.bullet_points.icon,
      type: snapshot.bulletPointsCount === currentBulletPointsCount ? 'content' : 'count',
      before: snapshot.bulletPointsCount,
      after: currentBulletPointsCount,
      delta: snapshot.bulletPointsCount === currentBulletPointsCount ? null : currentBulletPointsCount - snapshot.bulletPointsCount > 0 ? `+${currentBulletPointsCount - snapshot.bulletPointsCount}` : `${currentBulletPointsCount - snapshot.bulletPointsCount}`,
    });
  }

  // Description: hash
  const currentDescriptionHash = hashString((currentMedia.description || '').trim());

  if (snapshot.descriptionHash !== currentDescriptionHash) {
    changedFields.push({
      field: 'description',
      label: fieldDisplayConfig.description.label,
      icon: fieldDisplayConfig.description.icon,
      type: 'content',
      before: null,
      after: null,
      delta: null,
    });
  }

  return {
    hasChanges: changedFields.length > 0,
    changedFields,
  };
};

const setEquals = (set1, set2) => {
  if (set1.size !== set2.size) return false;
  for (let item of set1) {
    if (!set2.has(item)) return false;
  }
  return true;
};

const hashString = (str) => {
  if (!str) return 0;
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
  }
  return Math.abs(hash).toString(16);
};

const summarizeChanges = (diffResult) => {
  if (!diffResult || !diffResult.changedFields || diffResult.changedFields.length === 0) {
    return null;
  }
  const count = diffResult.changedFields.length;
  return `${count} field${count > 1 ? 's' : ''} changed`;
};

export { diffMediaSnapshot, fieldDisplayConfig, summarizeChanges };
